import {API, GraphQLQuery} from "@aws-amplify/api";
import React, {FC, useContext, useEffect, useMemo, useState} from "react";
import {getAccountToResource, listAccountToResources} from "../graphql/queries";
import {AccountToResource, BatchGetEc2PricesMutation, Ec2Price, Ec2ResourceReq, GetAccountToResourceQuery, ListAccountInfosQueryVariables, ListAccountToResourcesQuery, ListAccountToResourcesQueryVariables, ResourceStatus, ScanStatus, Summary} from "../API";
import {useInfiniteQuery, useQuery} from "@tanstack/react-query";
import {accountId} from "../accounts";
import {scanIndex} from "./ScanHistory";
import {Button, Header, Icon, List, Pagination, Popup, Table} from "semantic-ui-react";
import {useBehaviorSubject} from "../hooks";
import {BehaviorSubject} from "rxjs";
import {batchGetEc2Prices} from "../graphql/mutations";
import { Link } from "react-router-dom";
import { O } from "./AddAccount";

function useBatchGetEc2Prices(resources: Ec2ResourceReq[]) {
  const dedupeResourcesSet = new Set(resources.map(res => `${res.region}##${res.type}`));
  const requestResources = Array.from(dedupeResourcesSet).map(str => ({
    region: str.split("##")[0],
    type: str.split("##")[1]
  } as Ec2ResourceReq))
  return useQuery({
    queryKey: [batchGetEc2Prices, resources],
    queryFn: async () => resources && resources.length > 0 && (await API.graphql<GraphQLQuery<BatchGetEc2PricesMutation>>({
      query: batchGetEc2Prices,
      variables: {
        resources: requestResources
      }
    })).data || null
  })
}

const FILTER_SHOW_TAG_KEYS = ['Name'];
function useListAllA2R() {
  const si = useContext(scanIndex)
  const aid = useContext(accountId)
  return useInfiniteQuery({
    queryKey: [listAccountToResources, aid, si],
    queryFn: async ({ pageParam = ''}) => {
      if (!si || !aid) return null;
      const variables: ListAccountToResourcesQueryVariables = {
        accountId: aid,
        resourceId: { beginsWith: `${si}_` }
      }
      if (!!pageParam) variables.nextToken = pageParam;
      return (await API.graphql<GraphQLQuery<ListAccountToResourcesQuery>>({
        query: listAccountToResources,
        variables
      }))?.data || null
    },
    getNextPageParam: (lastPage, pages) => lastPage?.listAccountToResources?.nextToken || null
  })
}

const PAGE_ITEM_NUM = 20;

export const ScanResult: FC = () => {
  const {isFetching, data, hasNextPage, fetchNextPage, isLoading, refetch, isFetchingNextPage} = useListAllA2R()
  const aid = useContext(accountId)
  const si = useContext(scanIndex)
  const [clicked, setClicked] = useBehaviorSubject(useMemo(() => new BehaviorSubject(false), [aid, si]))
  const [pageIdx, setPageIdx] = useState(0)
  const [results, setResults] = useState<AccountToResource[]>([])
  const [toggleDateRel, setToggleDateRel] = useState(false) // false to show date string, true to show XX days ago
  const [scanSummary, setScanSummary] = useState<Summary | string | null>(null)

  const {data: prices, refetch: refetchPrices} = useBatchGetEc2Prices(results.map(res => ({
    type: res?.resourcePriceType,
    region: res?.resourceRegion
  } as Ec2ResourceReq)))

  const fetchScanSummary = async () => {
    try {
      setScanSummary('loading')
      const summaryResp = await API.graphql<GraphQLQuery<GetAccountToResourceQuery>>({
        query: getAccountToResource,
        variables: {
          accountId: aid,
          resourceId: `ec2_summary_${si}`
        }
      })
      setScanSummary(summaryResp.data?.getAccountToResource?.summary as Summary)
    } catch (error) {
      setScanSummary('Failed to load summary, please retry')
    }
  }

  const isSummaryLoading = scanSummary === 'loading'

  useEffect(() => {
    if (si && si.length > 0) {
      fetchScanSummary()
    }
  }, [si])

  useEffect(() => {
    if (!hasNextPage) {
      const items = data?.pages
        ?.map(p => p?.listAccountToResources?.items || [])
        ?.reduce((p,c) => p.concat(c), []) || [] 
      setResults(items as AccountToResource[])
    }
  }, [hasNextPage, si, data])

  if (hasNextPage) {
    if (!isLoading && !isFetching && !isFetchingNextPage) {
      fetchNextPage()
    }
    return <div>Fetching page {data?.pageParams?.length || 0}</div>
  }

  const ec2Prices: Ec2Price[] = prices ? JSON.parse(prices.batchGetEc2Prices?.body || '[]') : []
  // { 'type_region': ec2Price }
  const ec2PriceMap: {[key:string]: Ec2Price} = {}
  ec2Prices.forEach(ec2Price => ec2PriceMap[`${ec2Price.instanceType}_${ec2Price.region}`] = ec2Price)

  const split3 = (resId: string) => {
    const parts = [];
    let part = '';
    for (let i = 0; i < resId.length; ++i) {
      if (resId[i] === '_') {
        parts.push(part);
        if (parts.length === 2) {
          parts.push(resId.substring(i + 1));
          break;
        }
        part = '';
        continue;
      }
      part += resId[i];
    }
    return parts;
  }

  const formatTags = (tags: string, filterKeys: string[]) => {
    try {
      const tagArr = JSON.parse(tags) as object[];
      return (
        <List>
          {tagArr
            .filter(tag => (filterKeys.length > 0 ? filterKeys.includes(Object.keys(tag)[0]) : true))
            .map(tag =>
              Object.entries(tag)
                .map(([key, value]) => <List.Item key={key}>
                  {filterKeys.length > 0 ? 
                    <div><b>{key}</b>: {value}</div>:
                  <>
                  <List.Header>{key}</List.Header>
                  <List.Description>{value}</List.Description></>}</List.Item>)
            )
          }
        </List>
      )
    } catch {
      return tags;
    }
  }

  const formatDate = (date: string) => {
    const d = new Date(date)
    if (!toggleDateRel) {
      return d.toUTCString()
    } else {
      // format the date to X hour ago, or X days ago, a string to show relative time from now
      const now = new Date()
      const timeDifference = now.getTime() - d.getTime();
      const seconds = Math.round(timeDifference / 1000);
      const minutes = Math.round(seconds / 60);
      const hours = Math.round(minutes / 60);
      const days = Math.round(hours / 24);
      const months = Math.round(days / 30);
      const years = Math.round(months / 12);
    
      if (Math.abs(seconds) < 60) {
        return seconds === 0 ? "now" : `${seconds} seconds ago`;
      } else if (Math.abs(minutes) < 60) {
        return `${minutes} minutes ago`;
      } else if (Math.abs(hours) < 24) {
        return `${hours} hours ago`;
      } else if (Math.abs(days) < 30) {
        return `${days} days ago`;
      } else if (Math.abs(months) < 12) {
        return `${months} months ago`;
      } else {
        return `${years} year ago`;
      }
    }
  }

  const findPricePerDay = (ec2: AccountToResource) => {    
    if (!ec2Prices || ec2Prices.length == 0) return -1
    const priceKey = `${ec2.resourcePriceType}_${ec2.resourceRegion}`
    if (priceKey in ec2PriceMap) {
      const ec2Price = ec2PriceMap[priceKey]
      return parseFloat(ec2Price.price) * 24
    }
    return -1
  }

  const getEc2PriceDetail = (ec2: AccountToResource) => {
    if (!ec2Prices || ec2Prices.length == 0) return null
    const priceKey = `${ec2.resourcePriceType}_${ec2.resourceRegion}`
    if (priceKey in ec2PriceMap) {
      const ec2Price = ec2PriceMap[priceKey] as any
      return (<List>
        {Object.keys(ec2Price).map((k,i) => <List.Item key={i}>
          <List.Header>{k}</List.Header>
          <List.Description>{ec2Price[k]}</List.Description></List.Item>)}
      </List>)
    }
    return null
  }

  const calculateTotalSave = () => {
    if (!results) return -1;
    if (ec2Prices.length == 0) return -1;
    return results.filter(res => res?.resourceStatus == ResourceStatus.IDLE)
      ?.map(res => findPricePerDay(res!))
      ?.filter(costPerDay => costPerDay > 0)
      ?.reduce((p, r) => p + r, 0) || -1;
  }
  const totalSavePerDay = calculateTotalSave();
  const idelInstanceCount = results.filter(res => res?.resourceStatus == ResourceStatus.IDLE)?.length || 0;

  const getEc2AwsLink = (region: string, ec2Id: string) => {
    return `https://${region}.console.aws.amazon.com/ec2/home?region=${region}#InstanceDetails:instanceId=${ec2Id}`
  }

  const renderNotStartedOrEmpty = () => {
    // check the type of scanSummary, if string, render the string, if Summary, show the summary
    if (typeof scanSummary === 'string' && !isSummaryLoading) {
      return <div>{scanSummary}</div>
    } else if ((scanSummary as Summary)?.status === ScanStatus.NONE) {
      return <div>
      <Header size="medium">Scan has not started</Header>
      </div>
    }

    // empty
    if (!!results && !results.length) {
      return <div>No active resource was found.</div>
    }

  }

  return (
    <div>
      <Button
        loading={isLoading || isSummaryLoading || (clicked && isFetching)}
        style={{marginBottom: 12}}
        onClick={() => {
          if (isLoading || isSummaryLoading) return
          setClicked(true)
          fetchScanSummary()
          refetch()
        }}>Refresh</Button>
      
      {totalSavePerDay > 0 && 
        <Header size="small">Shutdown {idelInstanceCount} IDLE instances to save ${Number(totalSavePerDay).toFixed(3)}/day, ${Number(totalSavePerDay*30).toFixed(3)}/month.</Header>
      }
      {renderNotStartedOrEmpty()}
      {!!results.length && (
        <div>
          <Table>
            <Table.Header>
              <Table.Row>
                {/* <Table.HeaderCell>Type</Table.HeaderCell> */}
                <Table.HeaderCell>ResourceId</Table.HeaderCell>
                <Table.HeaderCell>Region</Table.HeaderCell>
                <Table.HeaderCell>CreatedAt</Table.HeaderCell>
                <Table.HeaderCell>Status</Table.HeaderCell>
                <Table.HeaderCell>Tags</Table.HeaderCell>
                <Table.HeaderCell>$ Per Day</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {results.slice(pageIdx*PAGE_ITEM_NUM, Math.min((pageIdx+1)*PAGE_ITEM_NUM,results.length))
                .filter(Boolean)
                .map((a2r) => (
                  <Table.Row key={a2r.resourceId} warning={a2r.resourceStatus === ResourceStatus.IDLE}>
                    {/* <Table.Cell>{split3(a2r.resourceId)[1]}</Table.Cell> */}
                    <Table.Cell><Link to={getEc2AwsLink(a2r.resourceRegion, split3(a2r.resourceId)[2])} target="_blank">{split3(a2r.resourceId)[2]}
                      <Icon style={{marginLeft: '0.5em'}} size="small" name="external"/></Link></Table.Cell>
                    <Table.Cell>{a2r.resourceRegion}</Table.Cell>
                    <Table.Cell><a onClick={() => setToggleDateRel(!toggleDateRel)}>{formatDate(a2r.resourceCreatedAt as any)}</a></Table.Cell>
                    <Table.Cell><b>{a2r.resourceStatus}</b></Table.Cell>
                    <Table.Cell>
                      {formatTags(a2r.tags as any, FILTER_SHOW_TAG_KEYS)}
                      {JSON.parse(a2r.tags || '[]').length > FILTER_SHOW_TAG_KEYS.length && <Popup trigger={<div>see all tags</div>}>
                        {formatTags(a2r.tags as any, [])}
                      </Popup>}
                    </Table.Cell>
                    <Table.Cell>
                      {findPricePerDay(a2r) !== -1 ?
                      <Popup trigger={<span>${findPricePerDay(a2r)}</span>}>
                        {getEc2PriceDetail(a2r)}
                      </Popup> : '-'}
                    </Table.Cell>
                  </Table.Row>
                ))}
            </Table.Body>
          </Table>
        </div>
      )}
      { results.length > PAGE_ITEM_NUM && 
      <Pagination totalPages={Math.ceil(results.length/PAGE_ITEM_NUM)} defaultActivePage={pageIdx}
        onPageChange={(e, {activePage}) => setPageIdx(activePage as number - 1)}/>
      }
    </div>
  )
}